import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { AxiosError, isAxiosError } from 'axios';
import { authApiClient } from '../../../api/axios';
import { showSnackbar } from '../../../api/snackbarSlice';

export const NOTIFICATION_PREFERENCES_QUERY_KEY = ['notificationPreferences'];

// GET notification preferences
export const fetchNotificationPreferences = async () => {
  const { data } = await authApiClient.get('/me/notification-preferences');
  return data;
};

// PATCH a single notification preference
export const updateNotificationPreference = async (key, value) => {
  const { data } = await authApiClient.patch('/me/notification-preferences', { [key]: value });

  return data;
};

// Fetch notification preferences
export const useNotificationPreferencesQuery = ({ enabled = true } = {}) => {
  const dispatch = useDispatch();

  const query = useQuery({
    queryKey: NOTIFICATION_PREFERENCES_QUERY_KEY,
    queryFn: fetchNotificationPreferences,
    enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (!query.isError) return;

    if (isAxiosError(query.error)) {
      dispatch(
        showSnackbar({
          message: query.error.response?.data?.errors?.message || query.error.message,
          severity: 'error',
        }),
      );
      return;
    }

    dispatch(
      showSnackbar({
        message: 'Failed to fetch notification preferences',
        severity: 'error',
      }),
    );
  }, [dispatch, query.error, query.isError]);

  return {
    notificationPreferences: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

// Update notification preferences
export const useUpdateNotificationPreferenceMutation = ({ onSuccess, onError } = {}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ key, value }) => updateNotificationPreference(key, value),

    onSuccess: (data, variables) => {
      queryClient.setQueryData(NOTIFICATION_PREFERENCES_QUERY_KEY, (oldData) => ({
        ...oldData,
        ...data,
      }));

      dispatch(
        showSnackbar({
          message: 'Notification preferences updated successfully',
          severity: 'success',
        }),
      );

      onSuccess && onSuccess(data, variables);
    },

    onError: (error, variables) => {
      if (error instanceof AxiosError) {
        dispatch(
          showSnackbar({
            message: error.response?.data?.errors?.message || error.message,
            severity: 'error',
          }),
        );
      } else {
        dispatch(
          showSnackbar({
            message: 'Failed to update notification preferences',
            severity: 'error',
          }),
        );
      }

      onError && onError(error, variables);
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_PREFERENCES_QUERY_KEY,
      });
    },
  });

  return {
    onSubmit: (key, value) => mutation.mutate({ key, value }),
    isPending: mutation.isPending,
  };
};
